/**
 * Version Control Service
 *
 * Handles version checking and update management for web application.
 * Version info is received from the notification API (no separate API call needed).
 */

import apiClient from './apiClient';

export interface VersionCheckResponse {
  updateAvailable: boolean;
  forceUpdate: boolean;
  updateType: 'optional' | 'recommended' | 'force';
  latestVersion: string;
  clientVersion: string;
  storeUrl?: string;
  updateTitle?: string;
  updateMessage?: string;
  releaseNotes?: string;
  releaseDate?: string;
  isMaintenanceMode: boolean;
  maintenanceMessage?: string;
  featureFlags?: Record<string, boolean>;
}

export interface VersionConfig {
  id: string;
  platform: 'web' | 'android' | 'ios';
  currentVersion: string;
  minimumVersion: string;
  updateType: 'optional' | 'recommended' | 'force';
  buildNumber?: number;
  storeUrl?: string;
  releaseNotes?: string;
  updateTitle?: string;
  updateMessage?: string;
  releaseDate?: string;
  isMaintenanceMode: boolean;
  maintenanceMessage?: string;
  featureFlags?: Record<string, boolean>;
  isActive: boolean;
  updatedAt: string;
}

export interface AllVersionsResponse {
  android?: VersionConfig;
  ios?: VersionConfig;
  web?: VersionConfig;
}

// Get current web version from environment
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

// Local storage keys
const VERSION_CHECK_KEY = 'version_check_result';
const VERSION_CHECK_TIMESTAMP = 'version_check_timestamp';

// Listeners for version info changes
type VersionListener = (versionInfo: VersionCheckResponse | null) => void;

class VersionControlService {
  private cachedResult: VersionCheckResponse | null = null;
  private lastCheckTime: number = 0;
  private listeners: Set<VersionListener> = new Set();

  /**
   * Subscribe to version info changes
   */
  subscribe(listener: VersionListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current value
    if (this.cachedResult) {
      listener(this.cachedResult);
    }
    // Return unsubscribe function
    return () => this.listeners.delete(listener);
  }

  /**
   * Set version info from external source (notification API)
   * This avoids making a separate API call for version check
   */
  setVersionInfo(versionInfo: VersionCheckResponse | null): void {
    if (versionInfo) {
      this.cachedResult = versionInfo;
      this.lastCheckTime = Date.now();
      localStorage.setItem(VERSION_CHECK_KEY, JSON.stringify(versionInfo));
      localStorage.setItem(VERSION_CHECK_TIMESTAMP, this.lastCheckTime.toString());
    }
    // Notify all listeners
    this.listeners.forEach(listener => listener(versionInfo));
  }

  /**
   * Get current cached version info (for initial render)
   */
  getCachedVersionInfo(): VersionCheckResponse | null {
    if (this.cachedResult) {
      return this.cachedResult;
    }
    // Try to load from localStorage
    const cachedData = localStorage.getItem(VERSION_CHECK_KEY);
    if (cachedData) {
      try {
        return JSON.parse(cachedData);
      } catch {
        return null;
      }
    }
    return null;
  }

  /**
   * Get current app version
   */
  getCurrentVersion(): string {
    return APP_VERSION;
  }

  /**
   * Get all version configs (admin only)
   */
  async getAllVersions(): Promise<AllVersionsResponse> {
    return await apiClient.get<AllVersionsResponse>('/version-control') as AllVersionsResponse;
  }

  /**
   * Save version config (admin only)
   */
  async saveVersionConfig(config: Partial<VersionConfig>): Promise<VersionConfig> {
    return await apiClient.post<VersionConfig>('/version-control', config) as VersionConfig;
  }

  /**
   * Update version config (admin only)
   */
  async updateVersionConfig(id: string, config: Partial<VersionConfig>): Promise<VersionConfig> {
    return await apiClient.put<VersionConfig>(`/version-control/${id}`, config) as VersionConfig;
  }

  /**
   * Toggle maintenance mode (admin only)
   */
  async toggleMaintenanceMode(
    platform: 'web' | 'android' | 'ios',
    isMaintenanceMode: boolean,
    maintenanceMessage?: string
  ): Promise<VersionConfig> {
    return await apiClient.post<VersionConfig>(
      `/version-control/${platform}/maintenance`,
      { isMaintenanceMode, maintenanceMessage }
    ) as VersionConfig;
  }

  /**
   * Initialize default versions (admin only)
   */
  async initializeDefaultVersions(): Promise<void> {
    await apiClient.post('/version-control/initialize');
  }

  /**
   * Perform hard reload to get new version
   */
  performUpdate(): void {
    // Clear all caches
    localStorage.removeItem(VERSION_CHECK_KEY);
    localStorage.removeItem(VERSION_CHECK_TIMESTAMP);

    // Clear service worker cache if exists
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }

    // Hard reload the page
    window.location.reload();
  }

  /**
   * Clear version check cache
   */
  clearCache(): void {
    this.cachedResult = null;
    this.lastCheckTime = 0;
    localStorage.removeItem(VERSION_CHECK_KEY);
    localStorage.removeItem(VERSION_CHECK_TIMESTAMP);
  }
}

export const versionControlService = new VersionControlService();
export default versionControlService;
